// JoinWorkspace - Page for joining workspace via room code
// Implements US-2.2: Join Workspace with Role Assignment

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { joinWorkspace } from '../services/workspaceService';
import styles from './JoinWorkspace.module.css';

/**
 * JOIN WORKSPACE page
 * - Allows authenticated users to join workspace via room code
 * - Validates room code and workspace status
 * - Determines role (user if creator, viewer otherwise)
 * - Redirects to workspace detail page on success
 */
export default function JoinWorkspace() {
  const navigate = useNavigate();
  const [roomCode, setRoomCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * HANDLE form submission
   * - Validates room code input
   * - Calls join endpoint
   * - Navigates to workspace on success
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // VALIDATE room code
    const trimmedCode = roomCode.trim();
    if (!trimmedCode) {
      setError('Bitte gib einen Raum Code ein.');
      return;
    }

    setIsLoading(true);

    try {
      // CALL join workspace endpoint
      const response = await joinWorkspace({ roomCode: trimmedCode });

      // NAVIGATE to workspace detail page
      // NOTE: Role will be displayed in workspace detail
      navigate(`/workspaces/${response.workspaceId}`);
    } catch (err) {
      // HANDLE errors (invalid code, closed workspace, etc.)
      setError(err instanceof Error ? err.message : 'Workspace konnte nicht beigetreten werden');
      setIsLoading(false);
    }
  };

  return (
    <Layout
      header={
        <>
          <div className={styles.headerLeft}>
            <img
              src={require('../assets/insurtale-logo.webp')}
              alt="insur_tale logo"
              className={styles.logo}
            />
            <h1 className={styles.appName}>Arbiter</h1>
          </div>
          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.backButton}
              onClick={() => navigate('/workspaces')}
            >
              ← Zurück zu Workspaces
            </button>
          </div>
        </>
      }
    >
      {/* PAGE title */}
      <div className={styles.pageHeader}>
        <h2 className={styles.pageTitle}>Workspace beitreten</h2>
      </div>

      <div className={styles.container}>
        <div className={styles.instructions}>
          <p>
            Gib den Raum Code ein, den du von der Workspace-Ersteller:in erhalten hast, um an der Arbiter-Session teilzunehmen.
          </p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label htmlFor="roomCode" className={styles.label}>
              Raum Code
            </label>
            <input
              id="roomCode"
              type="text"
              value={roomCode}
              onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
              placeholder="z.B. ABC12XYZ"
              className={styles.input}
              disabled={isLoading}
              maxLength={8}
              autoFocus
            />
            <p className={styles.hint}>
              Raum Codes sind 8 Zeichen lang und Groß-/Kleinschreibung wird ignoriert.
            </p>
          </div>

          {error && (
            <div className={styles.errorNotice}>
              <strong>Fehler:</strong> {error}
            </div>
          )}

          <button type="submit" className={styles.submitButton} disabled={isLoading}>
            {isLoading ? 'Trete bei...' : 'Workspace beitreten'}
          </button>
        </form>
      </div>
    </Layout>
  );
}
